/* Cars4Part cart drawer */
(() => {
  if (window.customElements.get('c4p-cart-drawer')) return;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const TIMER_KEY = 'c4p_cart_reserved_until';

  const storage = {
    get(key) {
      try { return window.localStorage.getItem(key); } catch (e) { return null; }
    },
    set(key, value) {
      try { window.localStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
    },
    remove(key) {
      try { window.localStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
    }
  };

  class C4pCartDrawer extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      this.panel = this.querySelector('.c4p-cart__panel');
      this.abort = new AbortController();
      const signal = this.abort.signal;

      // Open from any cart trigger in the theme
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[aria-controls="cart-drawer"]');
        if (!trigger || this.contains(trigger)) return;
        event.preventDefault();
        this.open();
      }, { signal });

      // Clicks inside the drawer
      this.addEventListener('click', (event) => this.onClick(event), { signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('input', (event) => this.onInput(event), { signal });
      this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
      this.addEventListener('submit', (event) => {
        const button = event.target.querySelector('.c4p-cart__checkout');
        button?.setAttribute('aria-busy', 'true');
      }, { signal });

      // Theme cart events (product forms, quick buy, etc.)
      document.addEventListener('cart:prepare-bundled-sections', (event) => {
        if (!event.detail.sections.includes(this.sectionId)) event.detail.sections.push(this.sectionId);
      }, { signal });

      document.addEventListener('cart:change', (event) => {
        const detail = event.detail || {};
        if (detail.c4p) return;
        const sections = detail.cart && detail.cart.sections;

        if (sections && sections[this.sectionId]) {
          this.render(sections[this.sectionId]);
        } else {
          this.refresh();
        }

        if (detail.baseEvent === 'variant:add') this.open();
      }, { signal });

      document.addEventListener('cart:refresh', () => this.refresh(), { signal });

      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.refresh();
      }, { signal });

      // Theme editor preview
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target.contains(this)) this.open();
      }, { signal });
      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.target.contains(this)) this.close();
      }, { signal });

      this.afterRender();
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearInterval(this.timerInterval);
      document.documentElement.classList.remove('c4p-cart-locked');
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    get itemCount() {
      return parseInt(this.getAttribute('data-item-count'), 10) || 0;
    }

    /* ---------------- Open / close ---------------- */

    open() {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.classList.remove('no-entrance');
      this.setAttribute('aria-hidden', 'false');
      // Force a reflow so the slide-in transition always runs
      void this.offsetWidth;
      this.classList.add('is-open');
      document.documentElement.classList.add('c4p-cart-locked');
      this.startTimer();
      this.updateUpsellArrows();
      setTimeout(() => this.panel.focus({ preventScroll: true }), 60);
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('c4p-cart-locked');
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus({ preventScroll: true });
      }
    }

    /* ---------------- Rendering ---------------- */

    async refresh() {
      const response = await fetch(`${root()}?section_id=${encodeURIComponent(this.sectionId)}`);
      if (!response.ok) return;
      this.render(await response.text());
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.querySelector('c4p-cart-drawer');
      const newContent = source && source.querySelector('[data-c4p-content]');
      const content = this.querySelector('[data-c4p-content]');
      if (!newContent || !content) return;

      const scroller = content.querySelector('.c4p-cart__scroll');
      const scrollTop = scroller ? scroller.scrollTop : 0;

      if (this.isOpen) this.classList.add('no-entrance');
      content.replaceWith(newContent);
      this.setAttribute('data-item-count', source.getAttribute('data-item-count') || '0');

      const newScroller = newContent.querySelector('.c4p-cart__scroll');
      if (newScroller) newScroller.scrollTop = scrollTop;

      this.afterRender();
    }

    afterRender() {
      const track = this.querySelector('[data-c4p-upsell-track]');
      if (track) {
        track.addEventListener('scroll', () => this.updateUpsellArrows(), { passive: true });
      }
      this.updateUpsellArrows();

      if (this.itemCount > 0) {
        if (this.isOpen) this.startTimer();
        else this.updateTimer();
      } else {
        storage.remove(TIMER_KEY);
        clearInterval(this.timerInterval);
      }
    }

    /* ---------------- Events ---------------- */

    onClick(event) {
      const target = event.target;

      if (target.closest('[data-c4p-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const qtyButton = target.closest('[data-c4p-qty]');
      if (qtyButton) {
        event.preventDefault();
        const line = qtyButton.closest('[data-key]');
        this.changeLine(line, parseInt(qtyButton.getAttribute('data-c4p-qty'), 10));
        return;
      }

      const addButton = target.closest('[data-c4p-add]');
      if (addButton) {
        event.preventDefault();
        this.addVariant(addButton);
        return;
      }

      if (target.closest('[data-c4p-upsell-prev]')) this.scrollUpsell(-1);
      if (target.closest('[data-c4p-upsell-next]')) this.scrollUpsell(1);
    }

    onChange(event) {
      const target = event.target;

      if (target.matches('[data-c4p-qty-input]')) {
        const value = Math.max(0, parseInt(target.value, 10) || 0);
        this.changeLine(target.closest('[data-key]'), value);
        return;
      }

      if (target.matches('[data-c4p-variant-select]')) {
        const card = target.closest('[data-c4p-card]');
        const option = target.selectedOptions[0];
        card.querySelector('[data-c4p-add]').setAttribute('data-c4p-add', option.value);

        const price = card.querySelector('[data-c4p-card-price]');
        const compare = option.getAttribute('data-compare');
        price.innerHTML = `${compare ? `<s>${compare}</s>` : ''}<span>${option.getAttribute('data-price')}</span>`;
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-c4p-note]')) return;
      clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = setTimeout(() => {
        fetch(`${root()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note })
        });
      }, 400);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key === 'Enter' && event.target.matches('[data-c4p-qty-input]')) {
        event.preventDefault();
        event.target.blur();
        return;
      }

      // Keep keyboard focus inside the drawer
      if (event.key === 'Tab' && this.isOpen) {
        const focusables = Array.from(this.panel.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, summary'))
          .filter((el) => el.offsetParent !== null);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    /* ---------------- Cart actions ---------------- */

    bundledSections() {
      const sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
      if (!sections.includes(this.sectionId)) sections.push(this.sectionId);
      return sections;
    }

    async changeLine(line, quantity) {
      if (!line || this.classList.contains('is-busy')) return;
      const key = line.getAttribute('data-key');

      this.classList.add('is-busy');
      if (quantity === 0) {
        line.classList.add('is-removing');
        await new Promise((resolve) => setTimeout(resolve, 260));
      }

      try {
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: this.bundledSections().join(',') })
        });
        const cart = await response.json();

        if (!response.ok) {
          line.classList.remove('is-removing');
          this.showError(cart.description || cart.message);
          await this.refresh();
          return;
        }

        this.applyCart(cart, 'line-item:change');
      } finally {
        this.classList.remove('is-busy');
      }
    }

    async addVariant(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');

      try {
        const formData = new FormData();
        formData.append('id', button.getAttribute('data-c4p-add'));
        formData.append('quantity', '1');
        formData.append('sections', this.bundledSections().join(','));

        const response = await fetch(`${root()}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const json = await response.json();

        if (!response.ok) {
          this.showError(json.description || json.message);
          return;
        }

        const cart = await (await fetch(`${root()}cart.js`)).json();
        cart.sections = json.sections;
        this.applyCart(cart, 'variant:add');
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    applyCart(cart, baseEvent) {
      if (cart.sections && cart.sections[this.sectionId]) {
        this.render(cart.sections[this.sectionId]);
      } else {
        this.refresh();
      }

      // Let the rest of the theme (cart counts, cart page...) know about the change
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent, cart, c4p: true }
      }));
    }

    showError(message) {
      if (!message) return;
      const footer = this.querySelector('.c4p-cart__footer');
      if (!footer) return;
      footer.querySelector('.c4p-cart__error')?.remove();

      const error = document.createElement('p');
      error.className = 'c4p-cart__error';
      error.setAttribute('role', 'alert');
      error.textContent = message;
      footer.prepend(error);
      setTimeout(() => error.remove(), 5000);
    }

    /* ---------------- Upsell carousel ---------------- */

    scrollUpsell(direction) {
      const track = this.querySelector('[data-c4p-upsell-track]');
      const card = track && track.firstElementChild;
      if (!card) return;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    updateUpsellArrows() {
      const track = this.querySelector('[data-c4p-upsell-track]');
      if (!track) return;
      const prev = this.querySelector('[data-c4p-upsell-prev]');
      const next = this.querySelector('[data-c4p-upsell-next]');
      const maxScroll = track.scrollWidth - track.clientWidth;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= maxScroll - 2;
    }

    /* ---------------- Reservation timer ---------------- */

    get timerMinutes() {
      return parseInt(this.getAttribute('data-timer-minutes'), 10) || 10;
    }

    deadline() {
      let until = parseInt(storage.get(TIMER_KEY), 10);
      const now = Date.now();
      const max = now + this.timerMinutes * 60000;

      if (!until || until <= now || until > max) {
        until = max;
        storage.set(TIMER_KEY, String(until));
      }
      return until;
    }

    startTimer() {
      clearInterval(this.timerInterval);
      if (this.itemCount === 0 || !this.querySelector('[data-c4p-timer]')) return;
      this.updateTimer();
      this.timerInterval = setInterval(() => {
        if (!this.isOpen) {
          clearInterval(this.timerInterval);
          return;
        }
        this.updateTimer();
      }, 1000);
    }

    updateTimer() {
      const el = this.querySelector('[data-c4p-timer]');
      if (!el || this.itemCount === 0) return;
      const remaining = Math.max(0, this.deadline() - Date.now());
      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      el.textContent = `${minutes}:${String(seconds).padStart(2, '0')}`;
    }
  }

  window.customElements.define('c4p-cart-drawer', C4pCartDrawer);
})();
